<!-- AddPaymentForm.vue -->
<script setup>
import { ref, reactive } from "vue";
import { usePaymentStore } from "@/stores/usePaymentStore";

import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "refresh"]);
const store = usePaymentStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const isLoading = ref(false);
const errorMessage = ref("");

const form = reactive({
  subscriptionId:       "",
  mpPaymentId:          "",
  baseAmount:           "",
  discountAmount:       "0",
  finalAmount:          "",
  couponId:             "",
  couponCodeSnapshot:   "",
  couponPercentSnapshot:"",
  planNameSnapshot:     "",
  planPriceSnapshot:    "",
  status:               "pending",
  statusDetail:         "",
  paymentMethod:        "",
});

async function handleSubmit() {
  errorMessage.value = "";

  // Validações mínimas
  if (!form.subscriptionId || isNaN(Number(form.subscriptionId))) {
    errorMessage.value = "ID da assinatura é obrigatório e deve ser numérico.";
    return;
  }
  if (form.baseAmount === "" || isNaN(Number(form.baseAmount))) {
    errorMessage.value = "Valor base é obrigatório e deve ser numérico.";
    return;
  }
  if (form.finalAmount === "" || isNaN(Number(form.finalAmount))) {
    errorMessage.value = "Valor final é obrigatório e deve ser numérico.";
    return;
  }
  if (!form.status.trim()) {
    errorMessage.value = "Status é obrigatório.";
    return;
  }

  isLoading.value = true;
  try {
    await store.createPayment({
      subscriptionId:        Number(form.subscriptionId),
      mpPaymentId:           form.mpPaymentId.trim() || null,
      baseAmount:            Number(form.baseAmount),
      discountAmount:        Number(form.discountAmount) || 0,
      finalAmount:           Number(form.finalAmount),
      couponId:              form.couponId !== "" ? Number(form.couponId) : null,
      couponCodeSnapshot:    form.couponCodeSnapshot.trim() || null,
      couponPercentSnapshot: form.couponPercentSnapshot !== ""
                               ? Number(form.couponPercentSnapshot)
                               : null,
      planNameSnapshot:      form.planNameSnapshot.trim() || null,
      planPriceSnapshot:     form.planPriceSnapshot !== ""
                               ? Number(form.planPriceSnapshot)
                               : null,
      status:                form.status.trim(),
      statusDetail:          form.statusDetail.trim() || null,
      paymentMethod:         form.paymentMethod.trim() || null,
    });
    emit("refresh");
  } catch (error) {
    errorMessage.value =
      error.response?.data?.message ||
      error.message ||
      "Erro ao criar payment.";
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="modal-backdrop" @click.self="emit('close')">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <!-- Cabeçalho -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Adicionar Pagamento</h2>
        <button @click="emit('close')" class="modal-close-btn" :disabled="isLoading">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0
                 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10
                 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293
                 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <!-- Corpo -->
      <div class="modal-body">
        <div v-if="errorMessage" class="form-error">{{ errorMessage }}</div>

        <div class="form-grid">
          <!-- Subscription ID -->
          <div class="form-field">
            <label class="form-label">ID Assinatura *</label>
            <input v-model="form.subscriptionId" type="number" min="1"
                   placeholder="ex: 42" class="form-input" />
          </div>

          <!-- MP Payment ID -->
          <div class="form-field">
            <label class="form-label">MP Payment ID</label>
            <input v-model="form.mpPaymentId" type="text"
                   placeholder="ex: 12345678901" class="form-input" />
          </div>

          <!-- Base Amount -->
          <div class="form-field">
            <label class="form-label">Valor Base *</label>
            <input v-model="form.baseAmount" type="number" min="0" step="0.01"
                   placeholder="ex: 29.90" class="form-input" />
          </div>

          <!-- Discount Amount -->
          <div class="form-field">
            <label class="form-label">Desconto</label>
            <input v-model="form.discountAmount" type="number" min="0" step="0.01"
                   placeholder="ex: 5.00" class="form-input" />
          </div>

          <!-- Final Amount -->
          <div class="form-field">
            <label class="form-label">Valor Final *</label>
            <input v-model="form.finalAmount" type="number" min="0" step="0.01"
                   placeholder="ex: 24.90" class="form-input" />
          </div>

          <!-- Status -->
          <div class="form-field">
            <label class="form-label">Status *</label>
            <select v-model="form.status" class="form-input form-select">
              <option value="none">—</option>
              <option value="pending">Pendente</option>
              <option value="approved">Aprovado</option>
              <option value="rejected">Rejeitado</option>
              <option value="cancelled">Cancelado</option>
              <option value="refunded">Estornado</option>
            </select>
          </div>

          <!-- Payment Method -->
          <div class="form-field">
            <label class="form-label">Método</label>
            <input v-model="form.paymentMethod" type="text"
                   placeholder="pix, credit_card, courtesy…" class="form-input" />
          </div>

          <!-- Coupon ID -->
          <div class="form-field">
            <label class="form-label">ID Cupom</label>
            <input v-model="form.couponId" type="number" min="1"
                   placeholder="ex: 3" class="form-input" />
          </div>

          <!-- Coupon Code Snapshot -->
          <div class="form-field">
            <label class="form-label">Código Cupom</label>
            <input v-model="form.couponCodeSnapshot" type="text"
                   placeholder="ex: PROMO10" class="form-input" />
          </div>

          <!-- Coupon Percent Snapshot -->
          <div class="form-field">
            <label class="form-label">% Desconto Cupom</label>
            <input v-model="form.couponPercentSnapshot" type="number"
                   min="0" max="100" placeholder="ex: 10" class="form-input" />
          </div>

          <!-- Plan Name Snapshot -->
          <div class="form-field">
            <label class="form-label">Plano (snapshot)</label>
            <input v-model="form.planNameSnapshot" type="text"
                   placeholder="ex: Pro Mensal" class="form-input" />
          </div>

          <!-- Plan Price Snapshot -->
          <div class="form-field">
            <label class="form-label">Preço Plano (snapshot)</label>
            <input v-model="form.planPriceSnapshot" type="number"
                   min="0" step="0.01" placeholder="ex: 29.90" class="form-input" />
          </div>

          <!-- Status Detail (largura total) -->
          <div class="form-field form-field-full">
            <label class="form-label">Detalhe</label>
            <input v-model="form.statusDetail" type="text"
                   maxlength="255" placeholder="Mensagem de status livre"
                   class="form-input" />
          </div>
        </div>
      </div>

      <!-- Rodapé -->
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="emit('close')" :disabled="isLoading">
          Cancelar
        </button>
        <button class="btn btn-primary" @click="handleSubmit" :disabled="isLoading">
            <svg v-if="isLoading" class="w-5 h-5 mr-2 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <svg v-else class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"/>
            </svg>
            <span>{{ isLoading ? "Salvando…" : "Adicionar Pagamento" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-panel {
  max-width: 680px;
  max-height: calc(100vh - 40px);
}

.modal-body {
  flex: 1;
}

/* ── Formulário ── */
.form-error {
  background: var(--danger-bg);
  border: 1px solid var(--danger);
  color: var(--danger-text);
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field-full {
  grid-column: 1 / -1;
}

.form-input {
  height: 34px;
}

.form-select {
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 18px;
  padding-right: 1.8rem;
}

</style>